import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Popper,
  Typography,
  Grow,
} from '@mui/material';
import { ExpandMore, ExpandLess, AddReaction, Close } from '@mui/icons-material';
import {
  collection, doc, onSnapshot, orderBy, query, setDoc, addDoc, deleteDoc, where, getDoc, updateDoc, arrayUnion, writeBatch,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { firestore } from '../firebaseSetup/firebase';
import { useUser } from '../providers/UserProvider';
import { GENDERS } from './AddCount';
import ScrollShadow from './ScrollShadow';

const REACTION_EMOJIS = ['🍆', '❤️', '👍', '🎉', '😈', '😇', '😞', '😡', '😮'];
const DEFAULT_EMOJI = REACTION_EMOJIS[0];

const triggerHaptic = async (style = 'medium') => {
  // Use web vibration API as fallback on all platforms
  if (navigator.vibrate) {
    navigator.vibrate(style === 'light' ? 5 : style === 'heavy' ? 20 : 10);
  }
};

const ReactionButton = ({ countUpdateId, fromUid }) => {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [canClickAway, setCanClickAway] = useState(false);
  const [myReaction, setMyReaction] = useState(null);
  const anchorRef = useRef(null);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  useEffect(() => {
    if (!user?.uid || !countUpdateId) return () => {};

    const reactionsQuery = query(
      collection(firestore, 'reactions', countUpdateId, 'items'),
      where('fromUid', '==', user.uid),
    );

    const unsub = onSnapshot(reactionsQuery, (snapshot) => {
      if (!snapshot.empty) {
        const reactionDoc = snapshot.docs[0];
        setMyReaction({ id: reactionDoc.id, ...reactionDoc.data() });
      } else {
        setMyReaction(null);
      }
    });

    return unsub;
  }, [countUpdateId, user?.uid]);

  const handleReact = useCallback(async (emoji) => {
    if (!user?.uid || !countUpdateId) return;

    triggerHaptic('light');

    if (myReaction?.emoji === emoji) {
      await deleteDoc(doc(firestore, 'reactions', countUpdateId, 'items', myReaction.id));
      setOpen(false);
      return;
    }

    if (myReaction) {
      await setDoc(doc(firestore, 'reactions', countUpdateId, 'items', myReaction.id), {
        fromUid: user.uid,
        fromName: user.name || 'Someone',
        fromAvatar: user.avatar || null,
        emoji,
        timestamp: new Date().toISOString(),
      });
    } else {
      await addDoc(collection(firestore, 'reactions', countUpdateId, 'items'), {
        fromUid: user.uid,
        fromName: user.name || 'Someone',
        fromAvatar: user.avatar || null,
        emoji,
        timestamp: new Date().toISOString(),
      });
    }
    setOpen(false);
  }, [countUpdateId, myReaction, user]);

  const handleTouchStart = useCallback(() => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      triggerHaptic('heavy');
      setCanClickAway(false);
      setOpen(true);
      // Allow click-away after finger is lifted
      setTimeout(() => setCanClickAway(true), 300);
    }, 700);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    // Only trigger quick tap reaction if long press didn't fire
    if (!longPressTriggered.current) {
      triggerHaptic('light');
      if (myReaction) {
        // If already has reaction, clear it
        deleteDoc(doc(firestore, 'reactions', countUpdateId, 'items', myReaction.id));
      } else {
        // No reaction yet, add default
        handleReact(DEFAULT_EMOJI);
      }
    }
    // Reset after a short delay to allow the picker to stay open
    setTimeout(() => {
      longPressTriggered.current = false;
    }, 100);
  }, [countUpdateId, handleReact, myReaction]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setCanClickAway(true);
    setOpen(true);
  }, []);

  const handleClearReaction = useCallback(async (e) => {
    e.stopPropagation();
    triggerHaptic('light');
    if (myReaction) {
      await deleteDoc(doc(firestore, 'reactions', countUpdateId, 'items', myReaction.id));
    }
    setOpen(false);
  }, [countUpdateId, myReaction]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    setCanClickAway(true);
    setOpen(true);
  }, []);

  // Don't show react button for own updates
  if (fromUid === user?.uid) return null;

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          fontSize: myReaction ? 20 : 18,
          color: myReaction ? 'inherit' : 'action.active',
        }}
      >
        {myReaction ? myReaction.emoji : <AddReaction fontSize="small" />}
      </IconButton>
      {/* Overlay to block scroll and interactions */}
      {open && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (canClickAway) setOpen(false);
          }}
          onTouchEnd={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1399,
            touchAction: 'none',
            bgcolor: 'rgba(0, 0, 0, 0.3)',
          }}
        />
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top"
        transition
        sx={{ zIndex: 1400 }}
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: 'calc(100vw - 16px)',
                overflow: 'hidden',
                p: 0,
              }}
            >
              {/* Scrollable emoji container with dynamic scroll shadows */}
              <ScrollShadow
                orientation="horizontal"
                shadowSize={20}
                sx={{ flex: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Find which emoji was clicked based on position
                  const scrollContainer = e.currentTarget.querySelector('[data-scroll-container]');
                  if (!scrollContainer) return;
                  const rect = scrollContainer.getBoundingClientRect();
                  const x = e.clientX - rect.left + scrollContainer.scrollLeft - 8; // 8px padding
                  const index = Math.floor(x / 48); // 44px width + 4px gap
                  if (index >= 0 && index < REACTION_EMOJIS.length) {
                    handleReact(REACTION_EMOJIS[index]);
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    p: '4px 8px',
                  }}
                >
                  {REACTION_EMOJIS.map((emoji) => (
                    <Box
                      key={emoji}
                      data-emoji={emoji}
                      sx={{
                        fontSize: '24px',
                        width: '44px',
                        height: '44px',
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: myReaction?.emoji === emoji ? 'action.selected' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        userSelect: 'none',
                      }}
                    >
                      {emoji}
                    </Box>
                  ))}
                </Box>
              </ScrollShadow>
              {/* Clear button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: 0.5,
                  pr: 1,
                  py: 0.5,
                  bgcolor: 'background.paper',
                }}
              >
                <IconButton
                  onClick={handleClearReaction}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

const ReactionsSummary = ({ countUpdateId }) => {
  const [reactions, setReactions] = useState([]);

  useEffect(() => {
    if (!countUpdateId) return () => {};

    const reactionsQuery = query(
      collection(firestore, 'reactions', countUpdateId, 'items'),
    );

    const unsub = onSnapshot(reactionsQuery, (snapshot) => {
      const reacts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReactions(reacts);
    });

    return unsub;
  }, [countUpdateId]);

  if (reactions.length === 0) return null;

  // Group and count by emoji, then sort by count descending
  const grouped = REACTION_EMOJIS.reduce((acc, emoji) => {
    const count = reactions.filter((r) => r.emoji === emoji).length;
    if (count > 0) {
      acc.push({ emoji, count });
    }
    return acc;
  }, []).sort((a, b) => b.count - a.count);

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {grouped.map(({ emoji, count }) => (
        <Box
          key={emoji}
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 14 }}>{emoji}</Typography>
          <Typography variant="caption" sx={{ ml: 0.25 }}>{count}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const GenderBreakdown = ({ genders }) => {
  if (!genders) return null;

  const entries = Object.entries(genders)
    .filter(([, count]) => count > 0)
    .sort((a, b) => {
      const orderA = GENDERS.find((g) => g.val === a[0])?.order ?? 99;
      const orderB = GENDERS.find((g) => g.val === b[0])?.order ?? 99;
      return orderA - orderB;
    });

  if (entries.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      {entries.map(([gender, count]) => {
        const genderInfo = GENDERS.find((g) => g.val === gender);
        return (
          <Box key={gender} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {genderInfo?.label || gender}:
            </Typography>
            <Typography variant="caption">{count}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const DetailedReactions = ({ countUpdateId }) => {
  const [reactions, setReactions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!countUpdateId) return () => {};

    const reactionsQuery = query(
      collection(firestore, 'reactions', countUpdateId, 'items'),
      orderBy('timestamp', 'desc'),
    );

    const unsub = onSnapshot(reactionsQuery, (snapshot) => {
      const reacts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReactions(reacts);
    });

    return unsub;
  }, [countUpdateId]);

  if (reactions.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        No reactions yet
      </Typography>
    );
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = typeof timestamp === 'string' ? new Date(timestamp) : (timestamp.toDate ? timestamp.toDate() : new Date(timestamp));
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <Box sx={{ mt: 1 }}>
      {reactions.map((reaction) => (
        <Box
          key={reaction.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          }}
        >
          <Typography sx={{ fontSize: 16 }}>{reaction.emoji}</Typography>
          <Avatar
            src={reaction.fromAvatar}
            alt={reaction.fromName}
            sx={{ width: 20, height: 20, cursor: 'pointer' }}
            onClick={() => reaction.fromUid && navigate(`/user/${reaction.fromUid}`)}
          />
          <Typography
            variant="caption"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => reaction.fromUid && navigate(`/user/${reaction.fromUid}`)}
          >
            {reaction.fromName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(reaction.timestamp)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const FollowRequestItem = ({ notification, onClose, highlighted = false }) => {
  const { user, following, pendingRequests, requests, followers } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Check if this request is still pending (not yet approved)
  const isPending = requests?.some((r) => r.from === notification.fromUid);

  // Check if the requester is now a follower (meaning request was approved, not cancelled)
  const isNowFollower = followers?.some((f) => f.uid === notification.fromUid);

  // Only show "Approved" if request is gone AND they're now a follower
  const wasApproved = !isPending && isNowFollower;

  // Check if we already follow this person
  const isFollowing = following?.some((f) => f.uid === notification.fromUid);
  // Check if we have a pending request to them
  const hasPendingRequest = pendingRequests?.some((pr) => pr.to === notification.fromUid);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = typeof timestamp === 'string' ? new Date(timestamp) : (timestamp.toDate ? timestamp.toDate() : new Date(timestamp));
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const handleApprove = useCallback(async (e) => {
    e.stopPropagation();
    if (!user?.uid || !notification.fromUid) return;
    setLoading(true);
    try {
      const requestQry = doc(firestore, 'requests', `${notification.fromUid}_${user.uid}`);
      const requestSnap = await getDoc(requestQry);
      if (requestSnap.exists()) {
        // Use batch write for atomic operation
        const batch = writeBatch(firestore);
        batch.update(doc(firestore, 'users', notification.fromUid), {
          following: arrayUnion(user.uid),
        });
        batch.delete(requestQry);
        await batch.commit();
      }
    } finally {
      setLoading(false);
    }
  }, [notification.fromUid, user?.uid]);

  const handleFollowBack = useCallback(async (e) => {
    e.stopPropagation();
    if (!user?.uid || !notification.fromUid || hasPendingRequest || isFollowing) return;
    setLoading(true);
    try {
      // If they're already a follower (we approved their request), we can directly follow back
      if (isNowFollower) {
        await updateDoc(doc(firestore, 'users', user.uid), {
          following: arrayUnion(notification.fromUid),
        });
      } else {
        // Check if they have a pending request to follow us - if so, auto-approve
        const theirRequestQry = doc(firestore, 'requests', `${notification.fromUid}_${user.uid}`);
        const theirRequestSnap = await getDoc(theirRequestQry);
        if (theirRequestSnap.exists()) {
          // They want to follow us, so we can directly add each other using batch
          const batch = writeBatch(firestore);
          batch.update(doc(firestore, 'users', user.uid), {
            following: arrayUnion(notification.fromUid),
          });
          batch.delete(theirRequestQry);
          await batch.commit();
        } else {
          // Create a follow request to them
          const requestQry = doc(firestore, 'requests', `${user.uid}_${notification.fromUid}`);
          await setDoc(requestQry, {
            from: user.uid,
            to: notification.fromUid,
            timestamp: new Date().toISOString(),
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [hasPendingRequest, isFollowing, isNowFollower, notification.fromUid, user?.uid]);

  const handleDismiss = useCallback(async (e) => {
    e.stopPropagation();
    if (!user?.uid || !notification.fromUid) return;
    setLoading(true);
    try {
      // Delete the request (cloud function will delete the notification)
      const requestQry = doc(firestore, 'requests', `${notification.fromUid}_${user.uid}`);
      await deleteDoc(requestQry);
      // Also delete the notification document directly for immediate feedback
      if (notification.id) {
        await deleteDoc(doc(firestore, 'userNotifications', user.uid, 'items', notification.id));
      }
    } finally {
      setLoading(false);
    }
  }, [notification.fromUid, notification.id, user?.uid]);

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    if (notification.fromUid) {
      onClose();
      navigate(`/user/${notification.fromUid}`);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: highlighted ? 'rgba(249, 151, 18, 0.15)' : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          src={notification.fromAvatar}
          alt={notification.fromName || 'User'}
          sx={{ width: 36, height: 36, cursor: 'pointer' }}
          onClick={handleAvatarClick}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: notification.read === false ? 600 : 400, fontSize: '0.85rem' }}>
            {notification.fromName || 'Someone'} wants to follow you
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(notification.timestamp)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
          {wasApproved ? (
            // Request was approved - show Follow Back if not already following
            !isFollowing ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleFollowBack}
                disabled={loading || hasPendingRequest}
                sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.75rem' }}
              >
                {hasPendingRequest ? 'Requested' : 'Follow Back'}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Following
              </Typography>
            )
          ) : isPending ? (
            // Request still pending - show Approve button only
            <>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={handleApprove}
                disabled={loading}
                sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.75rem' }}
              >
                Approve
              </Button>
              <IconButton
                size="small"
                onClick={handleDismiss}
                disabled={loading}
                sx={{ ml: 0.5 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const NotificationItem = ({ item, isOwnUpdate, onClose, highlighted = false, showAvatar = true }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getMessage = () => {
    if (isOwnUpdate) {
      if (item.countChange > 1) {
        return `You updated your bodycount (+${item.countChange})`;
      }
      return 'You updated your bodycount (+1)';
    }
    if (item.countChange > 1) {
      return `${item.fromName} updated their bodycount (+${item.countChange})`;
    }
    return `${item.fromName} updated their bodycount (+1)`;
  };

  const handleRowClick = () => {
    setExpanded(!expanded);
  };

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    const targetUid = isOwnUpdate ? user?.uid : item.fromUid;
    if (targetUid) {
      onClose();
      navigate(`/user/${targetUid}`);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: highlighted ? 'rgba(249, 151, 18, 0.15)' : 'transparent',
        cursor: 'pointer',
      }}
      onClick={handleRowClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {showAvatar && (
          <Avatar
            src={isOwnUpdate ? user?.avatar : item.fromAvatar}
            alt={isOwnUpdate ? user?.name : item.fromName}
            sx={{ width: 36, height: 36, cursor: 'pointer' }}
            onClick={handleAvatarClick}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: item.read === false ? 600 : 400, fontSize: '0.85rem' }}>
                {getMessage()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTime(item.timestamp)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {!isOwnUpdate && (
                <ReactionButton
                  countUpdateId={item.countUpdateId || item.id}
                  fromUid={item.fromUid}
                />
              )}
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <ReactionsSummary countUpdateId={item.countUpdateId || item.id} />
          </Box>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: showAvatar ? 6 : 0, pt: 1 }}>
          {isOwnUpdate ? (
            <DetailedReactions countUpdateId={item.id} />
          ) : (
            <GenderBreakdown genders={item.changeGenders} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const NotificationsDrawer = ({ open, onClose }) => {
  const { user, updateLastAppOpen, tappedNotification, setTappedNotification } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [ownUpdates, setOwnUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const wasOpenRef = useRef(false);
  const [highlightedIds, setHighlightedIds] = useState(new Set());
  const tappedNotificationRef = useRef(tappedNotification);

  // Keep ref in sync with tappedNotification
  useEffect(() => {
    tappedNotificationRef.current = tappedNotification;
  }, [tappedNotification]);

  // Update lastAppOpen and clear highlights when drawer closes
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      updateLastAppOpen();
      setHighlightedIds(new Set());
      setTappedNotification(null);
    }
    wasOpenRef.current = open;
  }, [open, updateLastAppOpen, setTappedNotification]);

  // Fetch notifications from others
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return () => {};
    }

    const notificationsQuery = query(
      collection(firestore, 'userNotifications', user.uid, 'items'),
      orderBy('timestamp', 'desc'),
    );

    let isFirstSnapshot = true;

    const unsub = onSnapshot(notificationsQuery, (snapshot) => {
      const notifs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setNotifications(notifs);
      setLoading(false);

      // On first snapshot when drawer opens, capture unread IDs for highlighting
      if (isFirstSnapshot && open) {
        const unreadIds = new Set(
          snapshot.docs.filter((d) => !d.data().read).map((d) => d.id),
        );

        // Also check for tapped notification from push and add matching notification to highlights
        const tapped = tappedNotificationRef.current;
        if (tapped) {
          snapshot.docs.forEach((d) => {
            const data = d.data();
            // Match by fromUid and type (count_update or follow_request)
            if (data.fromUid === tapped.fromUid) {
              if (tapped.type === 'count_update' && data.type === 'count_update') {
                unreadIds.add(d.id);
              } else if (tapped.type === 'follow_request' && data.type === 'follow_request') {
                unreadIds.add(d.id);
              }
            }
          });
        }

        setHighlightedIds(unreadIds);
        isFirstSnapshot = false;
      }

      // Mark unread as read when drawer is open
      if (open) {
        snapshot.docs.forEach((d) => {
          if (!d.data().read) {
            setDoc(doc(firestore, 'userNotifications', user.uid, 'items', d.id), { read: true }, { merge: true });
          }
        });
      }
    });

    return unsub;
  }, [user?.uid, open]);

  // Fetch own count updates
  useEffect(() => {
    if (!user?.uid) return () => {};

    const updatesQuery = query(
      collection(firestore, 'countUpdates'),
      where('fromUid', '==', user.uid),
      orderBy('timestamp', 'desc'),
    );

    const unsub = onSnapshot(updatesQuery, (snapshot) => {
      const ups = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOwnUpdates(ups);
    });

    return unsub;
  }, [user?.uid]);

  // Merge and sort all items by timestamp
  // follow_request type notifications are handled specially with FollowRequestItem
  const allItems = [
    ...notifications.map((n) => ({ ...n, itemType: n.type === 'follow_request' ? 'followRequest' : 'notification' })),
    ...ownUpdates.map((u) => ({ ...u, itemType: 'ownUpdate' })),
  ]
    .sort((a, b) => {
      const timeA = a.timestamp?.toMillis?.() || a.timestamp?.getTime?.() || new Date(a.timestamp).getTime() || 0;
      const timeB = b.timestamp?.toMillis?.() || b.timestamp?.getTime?.() || new Date(b.timestamp).getTime() || 0;
      return timeB - timeA;
    });

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          mt: 'calc(env(safe-area-inset-top) + 56px)',
          maxHeight: '70vh',
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        },
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : allItems.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No notifications yet</Typography>
        </Box>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          {allItems.map((item, index) => (
            <React.Fragment key={item.id}>
              {item.itemType === 'followRequest' ? (
                <FollowRequestItem
                  notification={item}
                  onClose={onClose}
                  highlighted={highlightedIds.has(item.id)}
                />
              ) : (
                <NotificationItem
                  item={item}
                  isOwnUpdate={item.itemType === 'ownUpdate'}
                  onClose={onClose}
                  highlighted={highlightedIds.has(item.id)}
                />
              )}
              {index < allItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Drawer>
  );
};

export default NotificationsDrawer;
export { NotificationItem };
